import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Info, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtMoney = (n) => (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const iso = (d) => d.toISOString().slice(0, 10);

const INCOME_CATS = ["ATM Commission", "Lottery Commission", "Rent Income", "Rebates", "Money Order Fees", "Other Income"];
const EXPENSE_CATS = ["Rent", "Utilities", "Payroll", "Insurance", "Bank Fees", "Maintenance", "Supplies", "Taxes & Licenses", "Other Expense"];
const PAY_METHODS = ["cash", "check", "ach", "card", "other"];

const EMPTY_FORM = { date: iso(new Date()), entry_type: "expense", category: "", description: "", payment_method: "cash", reference: "", amount: "", notes: "" };

export default function IncomeExpenseTab({ highlightId }) {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(iso(firstOfMonth));
  const [endDate, setEndDate] = useState(iso(today));
  const [typeFilter, setTypeFilter] = useState("");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (typeFilter) params.entry_type = typeFilter;
      const res = await axios.get(`${API}/income-expense`, { params });
      setEntries(res.data?.entries || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load entries");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter an amount greater than 0"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/income-expense/${editingId}`, form);
        toast.success("Entry updated");
      } else {
        await axios.post(`${API}/income-expense`, form);
        toast.success("Entry added");
      }
      setForm({ ...EMPTY_FORM, date: form.date, entry_type: form.entry_type });
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (e) => {
    setEditingId(e.id);
    setForm({ date: e.date, entry_type: e.entry_type, category: e.category || "", description: e.description || "", payment_method: e.payment_method || "cash", reference: e.reference || "", amount: String(e.amount), notes: e.notes || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/income-expense/${deleteTarget.id}`);
      toast.success("Entry deleted");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const totalIncome = entries.filter((e) => e.entry_type === "income").reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const totalExpense = entries.filter((e) => e.entry_type === "expense").reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const cats = form.entry_type === "income" ? INCOME_CATS : EXPENSE_CATS;
  const selCls = "h-9 px-2 rounded-md border border-border bg-background font-mono text-xs w-full";

  return (
    <div className="space-y-4" data-testid="income-expense-tab">
      <div className="bg-sky-500/10 border border-sky-500/40 rounded-lg px-4 py-2.5 flex items-start gap-2" data-testid="ie-dedupe-note">
        <Info className="w-4 h-4 text-sky-300 flex-shrink-0 mt-0.5" />
        <p className="font-mono text-xs text-sky-200">
          Sales (Z-Report) and Supplier Payments are pulled into reports <strong>automatically</strong> — do NOT re-enter them here or they would be counted twice. Use this tab only for other income (rent, commissions…) and other expenses (utilities, payroll…).
        </p>
      </div>

      {/* Entry form */}
      <Card className="border-primary/40 bg-primary/5">
        <CardContent className="p-4">
          <p className="font-mono text-xs font-bold text-primary mb-3">{editingId ? "Edit entry" : "New entry"}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">Date</label>
              <input type="date" value={form.date} onChange={(e) => setF("date", e.target.value)} className={selCls} data-testid="ie-form-date" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">Type</label>
              <select value={form.entry_type} onChange={(e) => setF("entry_type", e.target.value)} className={selCls} data-testid="ie-form-type">
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">Category</label>
              <Input list="ie-cat-list" value={form.category} onChange={(e) => setF("category", e.target.value)} placeholder="e.g. Utilities" className="h-9 font-mono text-xs" data-testid="ie-form-category" />
              <datalist id="ie-cat-list">{cats.map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">Amount ($)</label>
              <Input inputMode="decimal" value={form.amount} onChange={(e) => setF("amount", e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0.00" className="h-9 font-mono text-xs text-right" data-testid="ie-form-amount" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">Payment method</label>
              <select value={form.payment_method} onChange={(e) => setF("payment_method", e.target.value)} className={selCls} data-testid="ie-form-method">
                {PAY_METHODS.map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">Reference #</label>
              <Input value={form.reference} onChange={(e) => setF("reference", e.target.value)} placeholder="check / invoice #" className="h-9 font-mono text-xs" data-testid="ie-form-reference" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">Description</label>
              <Input value={form.description} onChange={(e) => setF("description", e.target.value)} placeholder="What is this for?" className="h-9 font-mono text-xs" data-testid="ie-form-description" />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">Notes</label>
              <Input value={form.notes} onChange={(e) => setF("notes", e.target.value)} placeholder="optional" className="h-9 font-mono text-xs" data-testid="ie-form-notes" />
            </div>
            <div className="flex items-end gap-2">
              {editingId && (
                <Button variant="outline" className="h-9 font-mono text-xs" onClick={() => { setEditingId(null); setForm(EMPTY_FORM); }} data-testid="ie-form-cancel">
                  Cancel
                </Button>
              )}
              <Button onClick={submit} disabled={saving} className="btn-3d h-9 flex-1 font-mono text-xs" data-testid="ie-form-submit">
                <Plus className="w-3.5 h-3.5 mr-1" /> {editingId ? "Update" : "Add entry"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters + totals */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={selCls} data-testid="ie-filter-start" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={selCls} data-testid="ie-filter-end" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">Type</label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={selCls} data-testid="ie-filter-type">
            <option value="">All</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>
        </div>
        <Button size="sm" variant="outline" className="btn-3d h-9" onClick={load} data-testid="ie-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
        <div className="ml-auto flex gap-4 font-mono text-xs">
          <span className="text-emerald-400">Income: <strong data-testid="ie-total-income">${fmtMoney(totalIncome)}</strong></span>
          <span className="text-rose-400">Expenses: <strong data-testid="ie-total-expense">${fmtMoney(totalExpense)}</strong></span>
        </div>
      </div>

      {/* Entries table */}
      <Card className="border-border/50">
        <CardContent className="p-2 overflow-x-auto">
          <table className="min-w-full text-xs font-mono tabular-nums" data-testid="ie-table">
            <thead>
              <tr className="border-b border-border/50 text-left">
                <th className="px-2 py-2">Date</th>
                <th className="px-2 py-2">Type</th>
                <th className="px-2 py-2">Category</th>
                <th className="px-2 py-2">Description</th>
                <th className="px-2 py-2">Method</th>
                <th className="px-2 py-2">Ref #</th>
                <th className="px-2 py-2 text-right">Amount</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No entries in this range yet.</td></tr>
              ) : entries.map((e, i) => (
                <tr key={e.id} className={`border-b border-border/20 hover:bg-muted/30 ${highlightId === e.id ? "bg-amber-500/10" : ""}`} data-testid={`ie-row-${i}`}>
                  <td className="px-2 py-1.5 whitespace-nowrap">{e.date}</td>
                  <td className="px-2 py-1.5">
                    <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${e.entry_type === "income" ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"}`}>
                      {e.entry_type}
                    </span>
                  </td>
                  <td className="px-2 py-1.5">{e.category || "—"}</td>
                  <td className="px-2 py-1.5 max-w-[220px] truncate" title={`${e.description}${e.notes ? ` · ${e.notes}` : ""}`}>{e.description || "—"}</td>
                  <td className="px-2 py-1.5 uppercase">{e.payment_method || "—"}</td>
                  <td className="px-2 py-1.5">{e.reference || "—"}</td>
                  <td className={`px-2 py-1.5 text-right font-bold ${e.entry_type === "income" ? "text-emerald-400" : "text-rose-400"}`}>${fmtMoney(e.amount)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <button onClick={() => startEdit(e)} className="p-1 text-sky-400 hover:text-sky-300" title="Edit" data-testid={`ie-edit-${i}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setDeleteTarget(e)} className="p-1 text-rose-400 hover:text-rose-300" title="Delete" data-testid={`ie-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="ie-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono">Delete this entry?</DialogTitle>
            <DialogDescription className="font-mono">
              {deleteTarget && (
                <>Delete <strong className="text-foreground">{deleteTarget.entry_type}</strong> of <strong className="text-foreground">${fmtMoney(deleteTarget.amount)}</strong> ({deleteTarget.category || "no category"}) on {deleteTarget.date}? This cannot be undone.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="ie-delete-cancel">Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="ie-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
